import { Customer, InputRuleApiModel } from '~/lib/api/smartsend/types';
import { EoriNumber, TaxId, VatNumber, VatStatus } from '../../lib/consts/ItemMetaConstants';
import { Individual, NotRegistered, Registered } from '../../lib/consts/TaxStatusConsts';
import SidePanelSection from '../../../../components/sidepanel-section';
import { MetaInputRow } from './MetaInputRow';
import { P2GGrid, P2GDropdown } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import useVatStatuses from './quotes/utils/use-vat-statuses';
import { CustomerVatFields } from '../../lib/helpers/SmartSendValidation';
import { useCallback, useMemo } from 'react';
import conditionalIsRequired from '../../lib/helpers/CustomerConditionalHelper';

interface props {
  customer: Customer;
  onUpdate: (customer: Customer, prop: string, value: any) => void;
  validation: InputRuleApiModel[];
  type: 'Sender' | 'Recipient';
  includeTitle?: boolean;
  wrapper: (content: React.ReactNode) => JSX.Element;
}

export const CustomerTaxDetails = ({ customer, onUpdate, validation, type, includeTitle, wrapper }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getStatuses } = useVatStatuses(TranslationNamespaces.SMARTSEND);
  const statuses = useMemo(() => getStatuses(type), [getStatuses, type]);
  const disableVatStatus =
    !validation.some((x) => x.name === VatStatus) && !statuses.some((x) => x.value === Individual);

  const manageChanged = useCallback((prop: string, v: any, u: Customer, statusOptions: string[]) => {
    // Remove the Company VAT Number if they change from Registered to another status
    if (prop === VatStatus && v !== Registered) {
      u.VatNumber = '';
    }

    if (prop === VatNumber) {
      // Set Tax Status if they provide a VAT Number
      if (v && u.VatStatus !== Registered) {
        u.VatStatus = Registered;
      }

      // Set Tax Status if they remove a VAT Number
      if (!v && u.VatStatus === Registered) {
        if (statusOptions.includes(Individual)) {
          u.VatStatus = Individual;
        } else if (statusOptions.includes(NotRegistered)) {
          u.VatStatus = NotRegistered;
        }
      }
    }
  }, []);

  const update = useCallback(
    (prop: string, v: any) => {
      const u = { ...customer };
      if (Object.keys(customer).includes(prop)) {
        u[prop] = v;
      } else {
        u.Meta[prop] = { Value: v, Type: 'String' };
      }

      manageChanged(
        prop,
        v,
        u,
        statuses.map((x) => x.value)
      );

      onUpdate(u, prop, v);
    },
    [customer, manageChanged, onUpdate, statuses]
  );

  const conditionalIsRequiredCallback = useCallback(
    (m: InputRuleApiModel) => conditionalIsRequired(m, customer),
    [customer]
  );

  if (!validation?.some((x) => CustomerVatFields.includes(x.name))) {
    return null;
  }

  return wrapper(
    <SidePanelSection
      title={
        includeTitle ? t(`${type.toUpperCase()}_DETAILS_SUBTITLE`, { defaultValue: `${type} Details:` }) : undefined
      }
    >
      <P2GGrid container spacing={2}>
        <P2GGrid item xs={6} sm={6}>
          <P2GDropdown
            value={customer.VatStatus}
            options={statuses}
            onChange={(v) => update(VatStatus, v)}
            id="Tax Status"
            required
            disabled={disableVatStatus}
            label={t('TAX_STATUS_LABEL', { defaultValue: 'Tax Status' })}
          />
        </P2GGrid>

        <MetaInputRow
          xs={6}
          sm={6}
          gridItem
          validation={validation}
          id={VatNumber}
          onChange={update}
          value={customer.VatNumber}
          required={customer.VatStatus === Registered ? true : undefined}
          isConditionalRequired={conditionalIsRequiredCallback}
          translationKey={
            customer ? `VAT_NUMBER_${customer.CountryIsoCode}_INPUT_LABEL`.toUpperCase() : 'VAT_NUMBER_INPUT_LABEL'
          }
        />

        <MetaInputRow
          xs={6}
          sm={6}
          gridItem
          validation={validation}
          id={EoriNumber}
          onChange={update}
          value={customer.Meta[EoriNumber]?.Value}
          translationKey={'EORI_NUMBER_INPUT_LABEL'}
        />

        <MetaInputRow
          xs={6}
          sm={6}
          gridItem
          validation={validation}
          id={TaxId}
          onChange={update}
          value={customer.Meta[TaxId]?.Value}
          translationKey={
            customer ? `TAX_ID_${customer.CountryIsoCode}_INPUT_LABEL`.toUpperCase() : 'TAX_ID_INPUT_LABEL'
          }
          isConditionalRequired={conditionalIsRequiredCallback}
        />
      </P2GGrid>
    </SidePanelSection>
  );
};
